import React, { useEffect } from "react";
import DataTable, { createTheme } from "react-data-table-component";
import TableLoader from "../../skeletons/TableLoader";
import { utcToLocal } from "../../../helpers/DateHelper";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";
import { useTheme } from "../../../context/ThemeContext";

const logoutLabel = (type) => {
  if (!type) {
    return "—";
  }
  const map = {
    manual: "Logged out",
    replaced: "New login",
    system: "System",
  };
  return map[type] || type;
};

function formatUserTypeLabel(row) {
  if (row?.urole_title) {
    return row.urole_title;
  }
  const type = row?.urole_type;
  if (!type) {
    return "";
  }
  return String(type)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function deviceLabel(row) {
  const parts = [];
  if (row.session_is_mobile == 1) {
    parts.push("Mobile");
  } else if (row.session_browser_name) {
    parts.push(row.session_browser_name);
  }
  if (row.session_os) {
    parts.push(row.session_os);
  }
  return parts.join(" · ") || "—";
}

export default function UserSessionLogsTable({
  data,
  loading,
  totalRows,
  page,
  limit,
  fetchData,
}) {
  const { theme } = useTheme();

  const columns = [
    {
      name: "#",
      width: "56px",
      cell: (row, index) => (
        <span className="text-muted">{((page - 1) * limit) + index + 1}.</span>
      ),
    },
    {
      name: "Team member",
      grow: 1.4,
      minWidth: "200px",
      selector: (row) => row.user_name,
      sortable: true,
      cell: (row) => {
        const name = (row.user_name || "").trim() || "Unknown user";
        const email = row.user_email || "—";
        const userType = formatUserTypeLabel(row);
        return (
          <div className="py-1" style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" title={name}>{name}</div>
            <div className="text-muted fs-12 text-truncate" title={email}>
              {userType ? `${userType} · ${email}` : email}
            </div>
          </div>
        );
      },
    },
    {
      name: "Logged in",
      width: "168px",
      selector: (row) => row.session_login_at,
      sortable: true,
      cell: (row) => (
        <span className="text-nowrap">{utcToLocal(row.session_login_at)}</span>
      ),
    },
    {
      name: "Session",
      width: "168px",
      cell: (row) =>
        row.session_logout_at ? (
          <div>
            <span className="badge bg-secondary-transparent text-secondary rounded-pill mb-1">
              Ended
            </span>
            <div className="fs-12 text-muted text-nowrap">
              {utcToLocal(row.session_logout_at)}
            </div>
          </div>
        ) : (
          <span className="badge bg-success text-white fw-semibold rounded-pill px-2 py-1">
            Active
          </span>
        ),
    },
    {
      name: "End reason",
      width: "120px",
      cell: (row) =>
        row.session_logout_at ? (
          <span className="fs-13">{logoutLabel(row.session_logout_type)}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      name: "IP address",
      width: "130px",
      selector: (row) => row.session_ip_address,
      cell: (row) => (
        <span className="font-monospace fs-12">{row.session_ip_address || "—"}</span>
      ),
    },
    {
      name: "Device",
      grow: 1,
      minWidth: "140px",
      cell: (row) => (
        <span className="fs-13 text-truncate d-block" title={deviceLabel(row)}>
          {deviceLabel(row)}
        </span>
      ),
    },
  ];

  useEffect(() => {
    createTheme("dark", {
      text: { primary: "#a2a3a4", secondary: "#2aa198" },
      background: { default: "#252729" },
      context: { background: "#cb4b16", text: "#a2a3a4" },
      divider: { default: "#073642" },
      action: {
        button: "rgba(0,0,0,.54)",
        hover: "rgba(0,0,0,.08)",
        disabled: "rgba(0,0,0,.12)",
      },
    });
  }, []);

  const handlePageChange = (newPage) => {
    fetchData(limit, newPage);
  };

  const handleLimitChange = (newLimit, newPage) => {
    fetchData(newLimit, newPage);
  };

  return (
    <DataTable
      className="cms-data-table"
      columns={columns}
      data={data}
      progressPending={loading}
      progressComponent={<TableLoader noOfColumn={columns.length} />}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationDefaultPage={page}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleLimitChange}
      paginationPerPage={limit}
      paginationRowsPerPageOptions={[10, 25, 50, 100]}
      fixedHeader
      fixedHeaderScrollHeight="520px"
      highlightOnHover
      pointerOnHover={false}
      theme={theme === "light" ? "light" : "dark"}
      customStyles={getCmsDataTableStyles(theme)}
    />
  );
}
